import {
  ChangeDetectionStrategy,
  Component,
  inject,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';

import { GettingStartedDrawerComponent } from '@core/getting-started-legacy';
import { TellentGettingStartedFacade } from '@core/tellent-getting-started-data';
import { TellentGettingStartedSubtaskViewModel } from '@core/tellent-getting-started-types';
import { TellentNavigationRoutingService } from '@core/tellent-logic';
import { TellentAdoptedStylesheetsDirective, TellentThemeDirective } from '@core/tellent-smart-ui';
import { TellentWebComponent } from '@core/tellent-types';
import { SpinnerComponent } from '@core/user-interface-legacy';

@Component({
  selector: 'x-getting-started-drawer',
  template: `@let isDrawerReady = svc.isDrawerReady();
    @let drawer = svc.drawer()!;
    @let steps = svc.steps()!;
    @let progressPct = svc.progressPct();
    @let showDismiss = svc.isDrawerTriggerVisible();

    @if (isDrawerReady) {
      <rt-getting-started-drawer
        [progress]="progressPct"
        [copy]="drawer"
        [steps]="steps"
        [activeStep]="activeStep()"
        [dismissGuideButtonVisible]="showDismiss!"
        (close)="close.emit()"
        (dismissGuide)="svc.dismissGuide(); dismiss.emit()"
        (subtaskCta)="subtaskCtaHandler($event)"
        (subtaskMarkAsDone)="subtaskMarkAsDoneHandler($event)"
        (toggleStep)="toggleStepHandler($event)"
        (toggleSubtask)="toggleSubtaskHandler($event)"
      />
    } @else {
      <rt-spinner />
    }`,
  imports: [GettingStartedDrawerComponent, SpinnerComponent],
  styleUrls: ['../../../styles.less'],
  styles: `
    :host {
      display: contents;
    }
  `,
  hostDirectives: [TellentThemeDirective, TellentAdoptedStylesheetsDirective],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TellentGettingStartedDrawerWrapComponent {
  public readonly svc = inject(TellentGettingStartedFacade);
  public readonly navSvc = inject(TellentNavigationRoutingService);
  public readonly activeStep = signal<[string | null, string | null]>([null, null]);

  public readonly close = output<void>();
  public readonly dismiss = output<void>();

  public readonly subtaskCta = output<{
    stepKey: string;
    subtask: TellentGettingStartedSubtaskViewModel;
  }>();

  public readonly subtaskMarkAsDone = output<{
    stepKey: string;
    subtask: TellentGettingStartedSubtaskViewModel;
  }>();

  public subtaskCtaHandler({ stepKey, subtaskKey }: { stepKey: string; subtaskKey: string }) {
    const [_, subtask] = this.svc.find(stepKey, subtaskKey);
    if (!subtask) return;

    switch (subtask.metadata.action?.kind) {
      case 'external_url':
        window.open(subtask.metadata.action.target, '_blank');
        break;

      case 'internal_url':
        this.navSvc.navigateById({
          id: subtask.metadata.action.target,
          appKind: subtask.metadata.action.appKind,
        });
        break;
      default:
        break;
    }

    this.subtaskCta.emit({ stepKey, subtask });
  }

  public subtaskMarkAsDoneHandler({
    stepKey,
    subtaskKey,
  }: {
    stepKey: string;
    subtaskKey: string;
  }) {
    const [_, subtask] = this.svc.find(stepKey, subtaskKey);
    if (!subtask) return;

    const [nextStep, nextSubtask] = this.svc.findNextPendingSubtask(stepKey, subtaskKey);
    this.activeStep.set([nextStep?.key ?? null, nextSubtask?.key ?? null]);

    this.svc.updateStepsProgress([subtaskKey]);
    this.subtaskMarkAsDone.emit({ stepKey, subtask });
  }

  public toggleStepHandler(stepKey: string | null) {
    const [currentStepKey] = this.activeStep();
    if (stepKey === currentStepKey) {
      this.activeStep.set([null, null]);
    } else {
      this.activeStep.set([stepKey, null]);
    }
  }

  public toggleSubtaskHandler([stepKey, subtaskKey]: [string | null, string | null]): void {
    const [currentStepKey, currentSubtaskKey] = this.activeStep();
    if (currentStepKey === stepKey && currentSubtaskKey === subtaskKey) {
      this.activeStep.set([stepKey, null]);
    } else {
      this.activeStep.set([stepKey, subtaskKey]);
    }
  }
}

export type TellentGettingStartedDrawerWebComponent = TellentWebComponent<
  TellentGettingStartedDrawerWrapComponent,
  'close' | 'dismiss' | 'subtaskCta' | 'subtaskMarkAsDone'
>;
